import React, { useState, useEffect } from "react";
import { HeroEditable } from "../../components/heroSection.jsx";
import Section from "../../components/base/Section.jsx";
import Input from "../../components/base/Input.jsx";
import Select from "../../components/base/Select.jsx";
import DateInfoBox from "../../components/dateInfoBox.jsx";
import FileUploadGPX from "../../components/FileUploadGPX.jsx";
import MapTrack from "../../components/MapTrack.jsx";
import RaceProfile from "../../components/RaceProfile.jsx";
import { theme } from "../../theme.js";
import { API_URL } from "../../utils/constants.js";

export default function CreateRaceGeneralInfos({raceInfo, setRaceInfo, extraInfo, setExtraInfo, selectedSportExtra, setSelectedSportExtra, handleRaceInfoChange, eventId, gpxTrackId, onGPXTrackIdChange}) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [sports, setSports] = useState([]);
  const [sportsData, setSportsData] = useState({}); // Store full sport objects by ID
  const [sportsById, setSportsById] = useState({}); // Store full sport objects by ID for lookup
  const [loadingSports, setLoadingSports] = useState(true);
  const [uploadedTrackData, setUploadedTrackData] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch existing GPX track data when editing
  useEffect(() => {
    if (gpxTrackId) {
      const fetchTrackData = async () => {
        try {
          const response = await fetch(`${API_URL}/api/tracks/${gpxTrackId}`);
          if (response.ok) {
            const data = await response.json();
            setUploadedTrackData(data.geojson_simplified);
          }
        } catch (error) {
          console.error("Error fetching track data:", error);
        }
      };
      fetchTrackData();
    }
  }, [gpxTrackId]);

  // Fetch sports from database
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await fetch(`${API_URL}/api/data/sports`);
        if (response.ok) {
          const data = await response.json();
          // Create mapping of sport names to full sport data
          const sportsMap = {};
          const sportsByIdMap = {};
          data.forEach(sport => {
            sportsMap[sport.name] = sport;
            sportsByIdMap[sport.id] = sport;
          });
          setSportsData(sportsMap);
          setSportsById(sportsByIdMap);
          
          // Sort sports by name for display
          const sortedSports = data.sort((a, b) => a.name.localeCompare(b.name));
          setSports(sortedSports);
        }
      } catch (error) {
        console.error("Error fetching sports:", error);
      } finally {
        setLoadingSports(false);
      }
    };
    fetchSports();
  }, []);

  // Handle sport change and update extraInfo
  useEffect(() => {
    if (raceInfo.sport) {
      const selectedSport = sportsById[raceInfo.sport];
      if (selectedSport) {
        const extraInfoSchema = selectedSport.extra_info || {};
        setSelectedSportExtra(extraInfoSchema);
        
        // Initialize sport-specific fields in extraInfo, preserving existing values
        const newExtraInfo = {};
        Object.keys(extraInfoSchema).forEach(key => {
          newExtraInfo[key] = extraInfo[key] || "";
        });
        setExtraInfo(newExtraInfo);
      }
    } else {
      setSelectedSportExtra({});
      setExtraInfo({});
    }
  }, [raceInfo.sport, sportsById]);
  
  const handleExtraInfoChange = (e) => {
    const { name, value } = e.target;
    setExtraInfo((prev) => ({ ...prev, [name]: isNaN(value) ? value : parseFloat(value) }));
  };
 
  return (
    <div style={styles.step1}>
        <div style={{ ...styles.mainColumn, padding: isMobile ? `${theme.spacing.lg} ${theme.spacing.sm}` : `${theme.spacing.massive} ${theme.spacing.lg}`, marginLeft: isMobile ? theme.spacing.sm : theme.spacing.huge, marginRight: isMobile ? theme.spacing.sm : theme.spacing.huge }}>
            <Section title="Informations générales">
            <div style={styles.formRow}>
              <Select
                name="sport"
                label = "Sport"
                dict = {Object.fromEntries(sports.map(s => [s.id, s.name]))}
                value={raceInfo.sport || ""}
                setFormData={setRaceInfo}
                required
              >
              </Select>
            </div>
            </Section>

            {/* Dynamic fields based on sport's extra_info */}
            {Object.keys(selectedSportExtra).length > 0 && (
              <Section title="Informations de l'épreuve">
                <div style={styles.dynamicFieldsContainer}>
                  {Object.entries(selectedSportExtra).map(([key, fieldInfo]) => {
                    const label = key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, char => char.toUpperCase());
                    const fieldType = fieldInfo.type === "float" ? "number" : "number";
                    const step = fieldInfo.type === "float" ? "0.1" : "1";
                    
                    return (
                      <div key={key} style={styles.formRow}>
                        <Input
                          label={label}
                          name={key}
                          type={fieldType}
                          step={step}
                          value={extraInfo[key] || ""}
                          setFormData={setExtraInfo}
                          onChange={handleExtraInfoChange}
                          required
                        />
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            <Section title="Autres informations">

            <Input 
                label="Date et heure de départ de la course"
                name="date"
                type="datetime-local"
                value={raceInfo.date}
                setFormData={setRaceInfo}
                width={"33%"}
                required
            />
            </Section>

            <Section title="Description">
            <Input
                name="description"
                label="Description"
                value={raceInfo.description}
                placeholder="Description de la course..."
                setFormData={setRaceInfo}
                multiline={true}
                required
            />
            </Section>

            <Section title="Parcours GPS">
            <FileUploadGPX 
                eventId={eventId}
                onGPXUpload={(trackData) => {
                  if (trackData && trackData.id) {
                    onGPXTrackIdChange(trackData.id);
                    setUploadedTrackData(trackData.geojson_simplified);
                  }
                }}
                onGPXChange={(file) => {
                  // Handle file selection if needed
                }}
            />
            {uploadedTrackData && (
              <div style={{ marginTop: theme.spacing.lg, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <MapTrack trackData={uploadedTrackData} title="Aperçu du parcours" />
                <RaceProfile trackData={uploadedTrackData} title="Profil d'élévation" />
              </div>
            )}
            </Section>
        </div>
    </div>
    );
}

const styles = {
  step1: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.radius.lg,
    overflow: "hidden",
  },
  mainColumn: {
    padding: `${theme.spacing.massive} ${theme.spacing.lg}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xxl,
    marginLeft: theme.spacing.huge,
    marginRight: theme.spacing.huge, 
  },
  formRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  label: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textMuted,
  },
  select: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.lg,
    fontSize: theme.fonts.size.sm,
    backgroundColor: theme.colors.white,
    color: theme.colors.textInverted,
    cursor: "pointer",
    transition: theme.transitions.base,
  },
  input: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.lg,
    fontSize: theme.fonts.size.sm,
    backgroundColor: theme.colors.white,
    color: theme.colors.textInverted,
  },
  dynamicFieldsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: theme.spacing.lg,
    width: "100%",
  },
};
